import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CustomCursor = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  const handleMouseMove = useCallback((e) => {
    setPosition({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseDown = useCallback(() => setIsClicking(true), []);
  const handleMouseUp = useCallback(() => setIsClicking(false), []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseDown, handleMouseUp]);

  useEffect(() => {
    const handleMouseOver = (e) => {
      // Check if in footer
      const isInFooter = e.target.closest("footer");
      if (isInFooter) return; // No effect in footer
      
      // Check if in header/nav area
      const isInHeader = e.target.closest("header");
      
      const target = e.target.closest("button, a, [role='button'], .cursor-hover");
      if (target && !target.closest('.no-shiny')) {
        target.classList.add("shiny-hover");
        
        // Keep cursor visible for header/nav, hide for other areas
        if (!isInHeader) {
          setIsHovering(true);
        }
      }
    };

    const handleMouseOut = (e) => {
      const isInFooter = e.target.closest("footer");
      if (isInFooter) return;
      
      const target = e.target.closest("button, a, [role='button'], .cursor-hover");
      if (target && !target.closest('.no-shiny')) {
        setIsHovering(false);
        target.classList.remove("shiny-hover");
      }
    };

    document.addEventListener("mouseover", handleMouseOver);
    document.addEventListener("mouseout", handleMouseOut);

    return () => {
      document.removeEventListener("mouseover", handleMouseOver);
      document.removeEventListener("mouseout", handleMouseOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide on mobile/touch devices
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  if (isTouchDevice) return null;

  return (
    <>
      {/* Default cursor - visible when not hovering */}
      <AnimatePresence>
        {!isHovering && (
          <>
            {/* Main cursor dot */}
            <motion.div
              className="fixed top-0 left-0 pointer-events-none z-[9999]"
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: 1,
                scale: isClicking ? 0.8 : 1,
                x: position.x - 6,
                y: position.y - 6,
              }}
              exit={{ opacity: 0, scale: 0, transition: { duration: 0.15 } }}
              transition={{
                opacity: { duration: 0.15 },
                scale: { duration: 0.1 },
                x: { type: "spring", stiffness: 2000, damping: 80 },
                y: { type: "spring", stiffness: 2000, damping: 80 },
              }}
            >
              <div className="w-3 h-3 bg-white rounded-full" style={{ boxShadow: '0 0 8px rgba(255,255,255,0.6)' }} />
            </motion.div>

            {/* Outer ring */}
            <motion.div
              className="fixed top-0 left-0 pointer-events-none z-[9998]"
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: 0.5,
                scale: 1,
                x: position.x - 20,
                y: position.y - 20,
              }}
              exit={{ opacity: 0, scale: 0, transition: { duration: 0.15 } }}
              transition={{
                opacity: { duration: 0.15 },
                scale: { duration: 0.1 },
                x: { type: "spring", stiffness: 800, damping: 50 },
                y: { type: "spring", stiffness: 800, damping: 50 },
              }}
            >
              <div className="w-10 h-10 rounded-full border border-white/40" />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Global styles for shiny button effect */}
      <style>{`
        * {
          cursor: none !important;
        }
        
        /* Footer uses default cursor */
        footer, footer * {
          cursor: auto !important;
        }
        
        /* Header uses custom cursor but stays visible */
        header, header * {
          cursor: none !important;
        }
        
        /* Shiny button effect - exclude nav links and footer */
        button:not(header button):not(footer button), 
        a:not(header a):not(footer a), 
        [role='button']:not(header [role='button']):not(footer [role='button']), 
        .cursor-hover:not(header .cursor-hover):not(footer .cursor-hover) {
          position: relative;
          overflow: hidden;
        }
        
        /* Theme-colored shine effect - exclude header and footer */
        button:not(header button):not(footer button)::before, 
        a:not(header a):not(footer a)::before, 
        [role='button']:not(header [role='button']):not(footer [role='button'])::before,
        .cursor-hover:not(header .cursor-hover):not(footer .cursor-hover)::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            120deg,
            transparent 0%,
            transparent 25%,
            rgba(59, 130, 246, 0.15) 40%,
            rgba(139, 92, 246, 0.4) 50%,
            rgba(59, 130, 246, 0.15) 60%,
            transparent 75%,
            transparent 100%
          );
          transform: skewX(-20deg);
          pointer-events: none;
          z-index: 10;
        }
        
        /* Stronger shine for white/light buttons */
        button.bg-white:not(header button):not(footer button)::before,
        button[class*="bg-white"]:not(header button):not(footer button)::before,
        a.bg-white:not(header a):not(footer a)::before,
        a[class*="bg-white"]:not(header a):not(footer a)::before {
          background: linear-gradient(
            120deg,
            transparent 0%,
            transparent 25%,
            rgba(59, 130, 246, 0.25) 40%,
            rgba(139, 92, 246, 0.6) 50%,
            rgba(59, 130, 246, 0.25) 60%,
            transparent 75%,
            transparent 100%
          );
        }
        
        button:not(header button):not(footer button).shiny-hover::before,
        a:not(header a):not(footer a).shiny-hover::before,
        [role='button']:not(header [role='button']):not(footer [role='button']).shiny-hover::before,
        .cursor-hover:not(header .cursor-hover):not(footer .cursor-hover).shiny-hover::before {
          animation: shineSwipe 0.6s ease-out forwards;
        }
        
        @keyframes shineSwipe {
          0% {
            left: -100%;
          }
          100% {
            left: 200%;
          }
        }
        
        /* Subtle scale and glow on hover - exclude header and footer */
        button:not(header button):not(footer button).shiny-hover,
        a:not(header a):not(footer a).shiny-hover,
        [role='button']:not(header [role='button']):not(footer [role='button']).shiny-hover,
        .cursor-hover:not(header .cursor-hover):not(footer .cursor-hover).shiny-hover {
          transform: scale(1.03);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        
        /* Theme-colored glow for dark buttons - keep text white */
        button:not(header button):not(footer button).shiny-hover:not(.bg-white):not([class*="bg-white"]),
        a:not(header a):not(footer a).shiny-hover:not(.bg-white):not([class*="bg-white"]) {
          box-shadow: 0 0 25px rgba(99, 102, 241, 0.3), 0 0 50px rgba(139, 92, 246, 0.15);
          color: white !important;
        }
        
        /* Blue/violet glow and gradient text for white buttons */
        button:not(header button):not(footer button).shiny-hover.bg-white,
        button:not(header button):not(footer button).shiny-hover[class*="bg-white"],
        a:not(header a):not(footer a).shiny-hover.bg-white,
        a:not(header a):not(footer a).shiny-hover[class*="bg-white"] {
          box-shadow: 0 0 30px rgba(99, 102, 241, 0.5), 0 0 60px rgba(139, 92, 246, 0.25);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
          background-clip: text !important;
        }
        
        button:not(header button):not(footer button).shiny-hover.bg-white span,
        button:not(header button):not(footer button).shiny-hover[class*="bg-white"] span,
        a:not(header a):not(footer a).shiny-hover.bg-white span,
        a:not(header a):not(footer a).shiny-hover[class*="bg-white"] span {
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
          background-clip: text !important;
        }
        
        button:not(header button):not(footer button).shiny-hover.bg-white svg,
        button:not(header button):not(footer button).shiny-hover[class*="bg-white"] svg {
          color: #8b5cf6 !important;
        }
        
        /* Header nav link underline animation */
        header nav button,
        header nav a:not([data-testid="header-cta-button"]) {
          position: relative;
          overflow: visible !important;
        }
        
        header nav button::after,
        header nav a:not([data-testid="header-cta-button"])::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 50%;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          transition: all 0.3s ease;
          transform: translateX(-50%);
          border-radius: 2px;
        }
        
        header nav button.shiny-hover::after,
        header nav a:not([data-testid="header-cta-button"]).shiny-hover::after {
          width: 100%;
        }
        
        header nav button.shiny-hover,
        header nav a:not([data-testid="header-cta-button"]).shiny-hover {
          color: white !important;
          transform: none !important;
        }
        
        /* Header CTA button - shiny effect */
        header [data-testid="header-cta-button"] {
          position: relative;
          overflow: hidden;
        }
        
        header [data-testid="header-cta-button"]::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            120deg,
            transparent 0%,
            transparent 25%,
            rgba(59, 130, 246, 0.25) 40%,
            rgba(139, 92, 246, 0.6) 50%,
            rgba(59, 130, 246, 0.25) 60%,
            transparent 75%,
            transparent 100%
          );
          transform: skewX(-20deg);
          pointer-events: none;
          z-index: 10;
        }
        
        header [data-testid="header-cta-button"].shiny-hover::before {
          animation: shineSwipe 0.6s ease-out forwards;
        }
        
        header [data-testid="header-cta-button"].shiny-hover {
          box-shadow: 0 0 25px rgba(99, 102, 241, 0.4);
          transform: scale(1.03);
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
          background-clip: text !important;
        }
        
        header [data-testid="header-cta-button"].shiny-hover span {
          background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
          background-clip: text !important;
        }
      `}</style>
    </>
  );
};

export default CustomCursor;
